import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './components/AuthContext';
import { ThemeProvider } from './components/ThemeContext';
import { LangProvider } from './components/LangContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ParticleBackground from './components/ParticleBackground';
import ProtectedRoute from './components/ProtectedRoute';
import GuestRoute from './components/GuestRoute';
import Preloader from './components/Preloader';
import ScrollToTop from './components/ScrollToTop';
import './styles/App.css';

// ===========================================================================
// All page components are now STATICALLY imported (no React.lazy / code
// splitting). The previous lazy-loading approach produced "Loading chunk N
// failed" runtime errors on iOS Safari because:
//
//   1. Safari aggressively caches `index.html` on disk + in its
//      back-forward cache (bfcache) and ignores `Cache-Control: no-cache`
//      in many situations.
//   2. After a server rebuild, the old index.html still references chunk
//      hashes that no longer exist — and Safari's bfcache will restore the
//      already-failed dynamic-import promises even after reload, putting
//      the app in an unrecoverable loop.
//
// Bundling everything into the main bundle is ~200 KB larger but eliminates
// the failure mode entirely. There are no chunks to fail.
// ===========================================================================
import Hero from './components/Hero';
import About from './components/About';
import Activities from './components/Activities';
import Achievements from './components/Achievements';
import Members from './components/Members';
import Blog from './components/Blog';
import Events from './components/Events';
import Contribute from './components/Contribute';
import LetsMove from './components/LetsMove';
import Recruitment from './components/Recruitment';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import NotFound from './components/NotFound';

// One-time cleanup: previous builds wrote `isp_chunk_reload_at` to
// sessionStorage + cookie as part of an auto-reload guard that proved
// problematic on iOS Safari. The auto-reload logic has been removed; clear
// any leftover values so they don't confuse anything else.
if (typeof window !== 'undefined') {
  try { sessionStorage.removeItem('isp_chunk_reload_at'); } catch (_) {}
  try { sessionStorage.removeItem('isp_chunk_reload'); } catch (_) {}
  try { document.cookie = 'isp_chunk_reload_at=; Path=/; Max-Age=0'; } catch (_) {}
}

// Error Boundary — minimal, no auto-reload (those caused infinite loops on
// iOS Safari due to bfcache restoring failed component state).
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, minHeight: '100vh', fontFamily: 'monospace', background: '#0a0a0f', color: '#00ff41' }}>
          <h1 style={{ fontSize: 22 }}>Runtime Error</h1>
          <p style={{ color: '#6aaa7c', marginTop: 12 }}>
            Đã xảy ra lỗi khi tải trang. Vui lòng thử lại.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20, padding: '10px 18px', background: '#00ff41',
              color: '#0a0a0f', border: 'none', borderRadius: 4, cursor: 'pointer',
              fontFamily: 'monospace', fontWeight: 700
            }}
          >
            🔄 Tải lại trang
          </button>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 20 }}>{this.state.error && this.state.error.toString()}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#6aaa7c', marginTop: 20 }}>{this.state.errorInfo && this.state.errorInfo.componentStack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Normalize duplicate slashes in the URL pathname
function URLNormalizer() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname.includes('//')) {
      const normalized = location.pathname.replace(/\/+/g, '/');
      navigate(normalized + location.search + location.hash, { replace: true });
    }
  }, [location, navigate]);
  return null;
}

// Screening watcher: when the current user has a pending pass/fail decision while
// screening is open, force them onto /letsmove regardless of the page they tried.
// (Useful for users who deep-link or refresh in the middle of a session.)
function ScreeningWatcher() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      let token = null;
      try { token = localStorage.getItem('isp_token'); } catch (_) { token = null; }
      if (!token) return;
      if (location.pathname === '/letsmove') return;
      fetch('/api/screening/my-decision', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (cancelled || !d || !d.hasDecision) return;
          if (d.acknowledgedAt) return;
          navigate('/letsmove', { replace: true });
        })
        .catch(() => {});
    };
    check();
    const t = setInterval(check, 30000); // re-check every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname, navigate]);
  return null;
}

function App() {
  const [lang, setLang] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/language')
      .then(res => res.json())
      .then(data => {
        setLang(data);
        setTimeout(() => setLoading(false), 2000);
      })
      .catch(err => {
        console.error('Failed to load language data:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Preloader />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <LangProvider value={lang}>
            <AuthProvider>
            <ScrollToTop />
            <URLNormalizer />
            <ScreeningWatcher />
            <div className="App">
              <ParticleBackground />
              <Navbar lang={lang} />
              <Routes>
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<Hero lang={lang} />} />
                <Route path="/about" element={<About lang={lang} />} />
                <Route path="/activities" element={<Activities lang={lang} />} />
                <Route path="/achievements" element={<Achievements lang={lang} />} />
                <Route path="/members" element={<Members lang={lang} />} />
                <Route path="/blog" element={<Blog lang={lang} />} />
                <Route path="/events" element={<Events lang={lang} />} />
                <Route path="/contribute" element={<Contribute lang={lang} />} />
                <Route path="/letsmove" element={<LetsMove lang={lang} />} />
                <Route path="/recruitment" element={<Recruitment lang={lang} />} />
                <Route path="/isper/:username" element={<Profile />} />
                <Route path="/login" element={
                  <GuestRoute><Login lang={lang} /></GuestRoute>
                } />
                <Route path="/forgot-password" element={
                  <GuestRoute><ForgotPassword lang={lang} /></GuestRoute>
                } />
                <Route path="/reset-password" element={
                  <GuestRoute><ResetPassword lang={lang} /></GuestRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard lang={lang} />
                  </ProtectedRoute>
                } />
                {/* Dedicated deep-link routes for the member-facing (/dashboard)
                    tabs so each member panel has its own URL. */}
                <Route path="/profile-settings" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="profile" /></ProtectedRoute>
                } />
                <Route path="/security" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="security" /></ProtectedRoute>
                } />
                <Route path="/qa" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="forum" /></ProtectedRoute>
                } />
                <Route path="/training-area" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="training" /></ProtectedRoute>
                } />
                <Route path="/ctf" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="training" initialTrainingMode="ctf" /></ProtectedRoute>
                } />
                <Route path="/resources" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="resources" /></ProtectedRoute>
                } />
                <Route path="/calendar" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="event_calendar" /></ProtectedRoute>
                } />
                <Route path="/nomination" element={
                  <ProtectedRoute><Dashboard lang={lang} initialTab="nomination" /></ProtectedRoute>
                } />
                <Route path="/setting" element={
                  <ProtectedRoute>
                    <Dashboard lang={lang} adminMode />
                  </ProtectedRoute>
                } />
                {/* Dedicated deep-link routes for each management panel.
                    Each opens /setting (admin mode) on the matching tab so the
                    panel has its own URL for direct navigation / bookmarking. */}
                <Route path="/membermanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="members" /></ProtectedRoute>
                } />
                <Route path="/blogmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="blogs" /></ProtectedRoute>
                } />
                <Route path="/eventmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="events_admin" /></ProtectedRoute>
                } />
                <Route path="/achievementmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="achievements" /></ProtectedRoute>
                } />
                <Route path="/recruitmentmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="recruitment" /></ProtectedRoute>
                } />
                <Route path="/contributormanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="contributors" /></ProtectedRoute>
                } />
                <Route path="/forummanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="forum_admin" /></ProtectedRoute>
                } />
                <Route path="/resourcemanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="resources_admin" /></ProtectedRoute>
                } />
                <Route path="/trainingmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="training" /></ProtectedRoute>
                } />
                <Route path="/ctfmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="training" initialTrainingMode="ctf" /></ProtectedRoute>
                } />
                <Route path="/screeningmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="screening" /></ProtectedRoute>
                } />
                <Route path="/nominationmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="nomination_admin" /></ProtectedRoute>
                } />
                <Route path="/mailmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="mail_campaign" /></ProtectedRoute>
                } />
                <Route path="/clubinfomanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="about_gallery" /></ProtectedRoute>
                } />
                <Route path="/calendarmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="event_calendar" /></ProtectedRoute>
                } />
                <Route path="/smtpmanager" element={
                  <ProtectedRoute><Dashboard lang={lang} adminMode initialTab="smtp" /></ProtectedRoute>
                } />
                <Route path="*" element={<NotFound lang={lang} />} />
              </Routes>
              <Footer lang={lang} />
            </div>
            </AuthProvider>
          </LangProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
